/**
 * VALIDADOR DE PAÍSES Y CIUDADES
 * Este archivo valida que no se pierdan países ni ciudades durante expansiones
 */

import { WorldCountry } from './types';

export const MINIMUM_COUNTRIES = 152; // Mantener los 152 países actuales
export const MINIMUM_CITIES = 11600; // Actualizado: ahora con Fase 14 tenemos más ciudades
export const TARGET_CITIES_PER_COUNTRY = 100;

export interface ValidationResult {
  isValid: boolean;
  countriesCount: number;
  totalCities: number;
  warnings: string[];
  errors: string[];
}

export interface ExpansionStats {
  totalCountries: number;
  countriesUnder100: number;
  countriesWith100Plus: number;
  percentageComplete: number;
  countriesNeedingExpansion: Array<{
    name: string;
    slug: string;
    currentCities: number;
    neededCities: number;
  }>;
}

/**
 * Obtiene estadísticas detalladas de expansión de ciudades
 */
export const getExpansionStats = (countries: WorldCountry[]): ExpansionStats => {
  const countriesUnder100 = countries.filter(country => country.topCities.length < 100);
  const countriesWith100Plus = countries.filter(country => country.topCities.length >= 100);
  
  const countriesNeedingExpansion = countriesUnder100.map(country => ({
    name: country.name,
    slug: country.slug,
    currentCities: country.topCities.length,
    neededCities: 100 - country.topCities.length
  })).sort((a, b) => b.neededCities - a.neededCities);

  return {
    totalCountries: countries.length,
    countriesUnder100: countriesUnder100.length,
    countriesWith100Plus: countriesWith100Plus.length,
    percentageComplete: Math.round((countriesWith100Plus.length / countries.length) * 100),
    countriesNeedingExpansion
  };
};

/**
 * Valida que el número de países y ciudades cumple con los mínimos
 */
export const validateCountryData = (countries: WorldCountry[]): ValidationResult => {
  const result: ValidationResult = {
    isValid: true,
    countriesCount: countries.length,
    totalCities: 0,
    warnings: [],
    errors: []
  };

  // Contar total de ciudades
  result.totalCities = countries.reduce((total, country) => total + country.topCities.length, 0);

  // Validar número mínimo de países
  if (result.countriesCount < MINIMUM_COUNTRIES) {
    result.isValid = false;
    result.errors.push(`Países insuficientes: ${result.countriesCount}/${MINIMUM_COUNTRIES}`);
  }

  // Validar número mínimo de ciudades
  if (result.totalCities < MINIMUM_CITIES) {
    result.isValid = false;
    result.errors.push(`Ciudades insuficientes: ${result.totalCities}/${MINIMUM_CITIES}`);
  }

  // Verificar países sin ciudades
  const countriesWithoutCities = countries.filter(country => country.topCities.length === 0);
  if (countriesWithoutCities.length > 0) {
    result.warnings.push(`Países sin ciudades: ${countriesWithoutCities.map(c => c.name).join(', ')}`);
  }

  // Verificar países con pocas ciudades
  const countriesWithFewCities = countries.filter(country => 
    country.topCities.length > 0 && country.topCities.length < 100
  );
  if (countriesWithFewCities.length > 0) {
    result.warnings.push(`Países con pocas ciudades (<100): ${countriesWithFewCities.length} países`);
  }

  // Verificar duplicados de slug
  const citySlugCounts = new Map<string, number>();
  countries.forEach(country => {
    country.topCities.forEach(city => {
      const count = citySlugCounts.get(city.slug) || 0;
      citySlugCounts.set(city.slug, count + 1);
    });
  });

  const duplicatedSlugs = Array.from(citySlugCounts.entries())
    .filter(([_, count]) => count > 1)
    .map(([slug, count]) => `${slug} (${count} veces)`);

  if (duplicatedSlugs.length > 0) {
    result.warnings.push(`Slugs de ciudades duplicados: ${duplicatedSlugs.join(', ')}`);
  }

  return result;
};

/**
 * Log detallado de validación con información de expansión
 */
export const logCountryValidation = (countries: WorldCountry[]): void => {
  const validation = validateCountryData(countries);
  const expansionStats = getExpansionStats(countries);
  
  console.log('🔍 VALIDACIÓN DE PAÍSES Y CIUDADES (FASE 14 POST-EXPANSIÓN):');
  console.log(`   ✅ Países: ${validation.countriesCount}/${MINIMUM_COUNTRIES}`);
  console.log(`   ✅ Ciudades: ${validation.totalCities}/${MINIMUM_CITIES}`);
  
  // Estadísticas de expansión detalladas
  console.log(`📈 ESTADÍSTICAS DE EXPANSIÓN A 100+ CIUDADES:`);
  console.log(`   🎯 Países con 100+ ciudades: ${expansionStats.countriesWith100Plus}/${expansionStats.totalCountries}`);
  console.log(`   ⏳ Países con menos de 100: ${expansionStats.countriesUnder100}`);
  console.log(`   📊 Progreso de expansión: ${expansionStats.percentageComplete}%`);
  
  // Mostrar top 10 países que más necesitan expansión
  console.log(`🔝 TOP 10 PAÍSES QUE MÁS NECESITAN EXPANSIÓN:`);
  expansionStats.countriesNeedingExpansion.slice(0, 10).forEach((country, index) => {
    console.log(`   ${index + 1}. ${country.name}: ${country.currentCities} ciudades (faltan ${country.neededCities})`);
  });
  
  if (validation.warnings.length > 0) {
    console.warn('⚠️ ADVERTENCIAS:');
    validation.warnings.forEach(warning => console.warn(`   - ${warning}`));
  }
  
  if (validation.errors.length > 0) {
    console.error('❌ ERRORES:');
    validation.errors.forEach(error => console.error(`   - ${error}`));
  }
  
  if (validation.isValid) {
    console.log('✅ Validación exitosa - Todos los mínimos cumplidos');
    console.log(`🎯 Fase 14 exitosa: ${expansionStats.countriesWith100Plus} países expandidos a 100+ ciudades`);
  } else {
    console.error('❌ Validación fallida - Revisar errores');
  }
};

/**
 * Asegura que siempre tengamos el mínimo de países
 */
export const ensureMinimumCountries = (countries: WorldCountry[]): WorldCountry[] => {
  if (countries.length >= MINIMUM_COUNTRIES) {
    return countries;
  }
  
  console.warn(`⚠️ Solo ${countries.length} países disponibles, se requieren ${MINIMUM_COUNTRIES}`);
  return countries; // Retornar lo que tenemos disponible
};

/**
 * Obtiene estadísticas de expansión de ciudades
 */
export const getCityExpansionStats = (countries: WorldCountry[]) => {
  const countriesNeedingExpansion = countries.filter(
    country => country.topCities.length < TARGET_CITIES_PER_COUNTRY
  );
  
  const totalCitiesNeeded = countriesNeedingExpansion.reduce(
    (total, country) => total + (TARGET_CITIES_PER_COUNTRY - country.topCities.length),
    0
  );
  
  return {
    countriesNeedingExpansion: countriesNeedingExpansion.length,
    totalCitiesNeeded,
    countriesWithTarget: countries.length - countriesNeedingExpansion.length,
    expansionProgress: `${countries.length - countriesNeedingExpansion.length}/${countries.length} países con 100+ ciudades`
  };
};

/**
 * Identifica países que necesitan expansión de ciudades
 */
export const getCountriesNeedingExpansion = (countries: WorldCountry[], targetCities: number = 100) => {
  return countries
    .filter(country => country.topCities.length < targetCities)
    .map(country => ({
      name: country.name,
      slug: country.slug,
      currentCities: country.topCities.length,
      neededCities: targetCities - country.topCities.length
    }))
    .sort((a, b) => b.neededCities - a.neededCities);
};
